// "Team time" — WI-11888 PR 3.
//
// Read-only view of time entries across users the caller is allowed to see.
// Backed by GET /time/entries?scope=<scope>&from=...&to=... — the visibility
// filter is enforced server-side (PR 2). The scope picker is gated so each
// option lights up only if the caller has access to it; the backend will
// 403 on scope=all unless Admin / Time Administrator.

import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import type { TimeEntry } from '@/types/api'

type Scope = 'team' | 'projects' | 'customers' | 'all'

const SCOPE_LABEL: Record<Scope, string> = {
  team:      'My team',
  projects:  'My projects',
  customers: 'My customers',
  all:       'All',
}

// ─── Date helpers (Mon-anchored week, ISO) ──────────────────────────────────

function toIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function startOfIsoWeek(d: Date): Date {
  const x = new Date(d)
  const day = (x.getDay() + 6) % 7  // 0 = Monday
  x.setDate(x.getDate() - day)
  x.setHours(0, 0, 0, 0)
  return x
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}

interface Props {
  /** Visibility flags from /me. Both default to true here so the parent stays
   *  the source of truth; the tab itself only renders when canViewOthersTime
   *  is true (see TimePage). */
  isTimeAdmin?: boolean
}

export default function TeamTimeView({ isTimeAdmin = false }: Props) {
  const [scope, setScope] = useState<Scope>('team')
  // Range defaults to the current ISO week (Mon-Sun).
  const today = useMemo(() => new Date(), [])
  const [weekStart, setWeekStart] = useState<Date>(() => startOfIsoWeek(today))
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart])
  const from = toIso(weekStart)
  const to   = toIso(weekEnd)

  const { data: entries = [], isLoading, isError, error } = useQuery({
    queryKey: ['time', 'entries', 'team', scope, from, to],
    queryFn:  () => api.get<TimeEntry[]>(`/time/entries?scope=${scope}&from=${from}&to=${to}`),
  })

  const totals = useMemo(() => {
    let worked = 0, billable = 0
    const staffSet = new Set<number>()
    for (const e of entries) {
      worked   += e.workedHrs
      billable += e.billableHrs
      staffSet.add(e.staffId)
    }
    return { worked, billable, staffCount: staffSet.size, entryCount: entries.length }
  }, [entries])

  // Group entries by staff for the table sections
  const grouped = useMemo(() => {
    const m = new Map<number, { staffName: string; rows: TimeEntry[] }>()
    for (const e of entries) {
      const k = e.staffId
      const existing = m.get(k)
      if (existing) existing.rows.push(e)
      else m.set(k, { staffName: e.staffName ?? `User #${e.staffId}`, rows: [e] })
    }
    // Sort: each staff's rows by date; staff blocks alphabetical.
    for (const v of m.values()) {
      v.rows.sort((a, b) => a.entryDate.localeCompare(b.entryDate))
    }
    return [...m.entries()].sort(([, a], [, b]) => a.staffName.localeCompare(b.staffName))
  }, [entries])

  const errMsg = (error as Error | null)?.message ?? ''
  const isForbidden = errMsg.toLowerCase().includes('403')

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
      {/* Controls: scope picker + week navigator */}
      <div className="row" style={{ gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div className="row" style={{ gap: 4 }}>
          {(['team', 'projects', 'customers'] as Scope[]).map(s => (
            <button
              key={s}
              className={`chip${scope === s ? ' active' : ''}`}
              onClick={() => setScope(s)}
            >
              {SCOPE_LABEL[s]}
            </button>
          ))}
          {isTimeAdmin && (
            <button
              className={`chip${scope === 'all' ? ' active' : ''}`}
              onClick={() => setScope('all')}
              title="Admin / Time Administrator only"
            >
              {SCOPE_LABEL.all}
            </button>
          )}
        </div>
        <div className="row" style={{ gap: 6, marginLeft: 'auto' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setWeekStart(addDays(weekStart, -7))}>← Prev week</button>
          <span className="mono" style={{ fontSize: 12.5, padding: '0 8px' }}>
            {from} → {to}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={() => setWeekStart(addDays(weekStart, 7))}>Next week →</button>
        </div>
      </div>

      {/* Totals strip */}
      <div className="stat-grid">
        <div className="stat">
          <div className="stat-label">Worked</div>
          <div className="stat-value tabular">{totals.worked.toFixed(1)}<span style={{ fontSize: 14, color: 'var(--ns-text-muted)' }}> h</span></div>
        </div>
        <div className="stat">
          <div className="stat-label">Billable</div>
          <div className="stat-value tabular">{totals.billable.toFixed(1)}<span style={{ fontSize: 14, color: 'var(--ns-text-muted)' }}> h</span></div>
        </div>
        <div className="stat">
          <div className="stat-label">Staff</div>
          <div className="stat-value tabular">{totals.staffCount}</div>
        </div>
        <div className="stat">
          <div className="stat-label">Entries</div>
          <div className="stat-value tabular">{totals.entryCount}</div>
        </div>
      </div>

      {/* Body */}
      {isLoading && <p className="muted" style={{ paddingTop: 24, textAlign: 'center' }}>Loading…</p>}
      {isError && isForbidden && (
        <p className="muted" style={{ paddingTop: 24, textAlign: 'center' }}>
          You don't have access to the "{SCOPE_LABEL[scope]}" scope. Pick another scope above.
        </p>
      )}
      {isError && !isForbidden && (
        <p className="muted" style={{ paddingTop: 24, textAlign: 'center', color: 'var(--ns-err)' }}>
          Failed to load entries: {errMsg}
        </p>
      )}
      {!isLoading && !isError && entries.length === 0 && (
        <p className="muted" style={{ paddingTop: 24, textAlign: 'center' }}>
          No entries in this scope for this week.
        </p>
      )}
      {!isLoading && !isError && entries.length > 0 && (
        <div className="card" style={{ padding: 0 }}>
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Date</th>
                <th>Customer / Project</th>
                <th>Phase / Activity</th>
                <th style={{ width: 80, textAlign: 'right' }}>Worked</th>
                <th style={{ width: 80, textAlign: 'right' }}>Billable</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map(([staffId, { staffName, rows }]) => (
                <StaffBlock key={staffId} staffName={staffName} rows={rows} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function StaffBlock({ staffName, rows }: { staffName: string; rows: TimeEntry[] }) {
  const worked   = rows.reduce((s, r) => s + r.workedHrs,   0)
  const billable = rows.reduce((s, r) => s + r.billableHrs, 0)
  return (
    <>
      <tr style={{ background: 'var(--ns-surface-2)' }}>
        <td colSpan={3} style={{ fontWeight: 600, fontSize: 12.5 }}>{staffName}</td>
        <td className="mono tabular" style={{ textAlign: 'right', fontWeight: 600 }}>{worked.toFixed(1)}h</td>
        <td className="mono tabular" style={{ textAlign: 'right', fontWeight: 600 }}>{billable.toFixed(1)}h</td>
      </tr>
      {rows.map(r => (
        <tr key={r.id}>
          <td className="mono" style={{ fontSize: 12 }}>{r.entryDate}</td>
          <td style={{ fontSize: 12.5 }}>
            <div>{r.projectName ?? <span className="muted">{r.leaveLabel ?? 'Leave'}</span>}</div>
            {r.customerName && <div className="muted" style={{ fontSize: 11 }}>{r.customerName}</div>}
          </td>
          <td className="muted" style={{ fontSize: 12 }}>
            {r.phaseName ?? '—'}{r.activityName ? ` · ${r.activityName}` : ''}
          </td>
          <td className="mono tabular" style={{ textAlign: 'right' }}>{r.workedHrs.toFixed(1)}</td>
          <td className="mono tabular" style={{ textAlign: 'right' }}>{r.billableHrs.toFixed(1)}</td>
        </tr>
      ))}
    </>
  )
}
